import * as THREE from "three";
import { track } from "./telemetry";
import {
  EMIT_BOUNCE_DEFAULT,
  EMIT_CONE_HALF_ANGLE,
  EMIT_DRAG_DEFAULT,
  EMIT_FRICTION_DEFAULT,
  EMIT_RATE_DEFAULT,
  EMIT_SPEED_MAX,
  EMIT_SPEED_MIN,
  f32,
  EMITTER_COLOR,
  EMITTER_GIZMO_HEIGHT,
  EMITTER_GIZMO_RADIUS,
  EMITTER_POSITION,
  LIFESPAN_MAX,
  LIFESPAN_MIN,
  MAX_EMITTERS,
  SEQ_MAX_ROWS,
  SEQ_NUM_DEFAULT,
} from "./constants";

const DEG = 180 / Math.PI;

export type Meter = 3 | 4;

/**
 * Columns of one sequencer bar: the meter's beats on a fixed eighth-note cell
 * grid (two cells per beat), so 4/4 → 8 columns and 3/4 → 6 columns.
 */
export function seqColumns(meter: Meter): number {
  return meter * 2;
}

/**
 * Nearest valid sequencer length for a meter: a whole number of bars
 * (multiples of the column count) between one bar and SEQ_MAX_ROWS bars.
 */
export function clampSeqNum(num: number, meter: Meter): number {
  const cols = seqColumns(meter);
  const rows = Math.min(SEQ_MAX_ROWS, Math.max(1, Math.round(num / cols)));
  return cols * rows;
}

/**
 * One particle source. `rotation` orients the emit direction: the base heading
 * is +Y rotated by this quaternion, so a 3D rotation aims the cone out of the
 * XY plane (in 2D the gizmo only offers a z-ring, keeping it in-plane, and the
 * z velocity is clamped by uZLock anyway). `coneDeg` is the half-angle of the
 * random spread around that heading. `lifeMin`/`lifeMax` bound the lifespan
 * (seconds) randomly assigned to each particle this emitter spawns. A scene
 * holds up to MAX_EMITTERS of these, each emitting on its own rate into the
 * shared particle buffer.
 *
 * `meter` / `num` shape the sequencer: `num` is the loop length in eighth-note
 * cells (a multiple of seqColumns(meter), one to SEQ_MAX_ROWS bars) and
 * `seqSteps` holds the active cell indices in 0..num-1.
 *
 * `pulseSpeedFromSettings` selects the launch speed of emitIn-driven emission:
 * false (default) inherits the inbound pulses' impact speed, true uses the
 * emitter's own speedMin/speedMax range instead.
 *
 * `gate` multiplies the free-Hz emit rate (0..1; the clock-grid / sequencer
 * paths ignore it). It is a wire-only settable param — no panel control, no
 * serialization — so it always rests at 1 unless a Param-in edge drives it.
 */
export interface Emitter {
  id: string;
  position: THREE.Vector3;
  rate: number;
  rotation: THREE.Quaternion;
  coneDeg: number;
  speedMin: number;
  speedMax: number;
  lifeMin: number;
  lifeMax: number;
  offset: number;
  bounce: number;
  friction: number;
  drag: number;
  clockSync: boolean;
  seqMode: boolean;
  meter: Meter;
  num: number;
  seqSteps: number[];
  kind: "sync" | "full";
  pulseSpeedFromSettings: boolean;
  gate: number;
  name: string;
}

export type EmitterParams = Omit<Emitter, "position" | "id">;

export function defaultEmitterParams(): EmitterParams {
  return {
    rate: EMIT_RATE_DEFAULT,
    rotation: new THREE.Quaternion(),
    coneDeg: EMIT_CONE_HALF_ANGLE * DEG,
    speedMin: EMIT_SPEED_MIN,
    speedMax: EMIT_SPEED_MAX,
    lifeMin: LIFESPAN_MIN,
    lifeMax: LIFESPAN_MAX,
    offset: 0,
    bounce: EMIT_BOUNCE_DEFAULT,
    friction: EMIT_FRICTION_DEFAULT,
    drag: EMIT_DRAG_DEFAULT,
    clockSync: false,
    seqMode: false,
    meter: 4,
    num: SEQ_NUM_DEFAULT,
    seqSteps: [],
    kind: "full",
    pulseSpeedFromSettings: true,
    gate: 1,
    name: "",
  };
}

export function defaultEmitter(): Emitter {
  return { id: crypto.randomUUID(), position: EMITTER_POSITION.clone(), ...defaultEmitterParams() };
}

/** Pattern lengths of every placed sequencer, the input to performanceCycleCells. */
export function sequencerNums(emitters: readonly Emitter[]): number[] {
  const nums: number[] = [];
  for (const em of emitters) {
    if (em.kind === "sync") {
      nums.push(em.num);
    }
  }
  return nums;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Parse fallbacks for emitters saved before bounce/friction existed: those
 * scenes ran with bounce=1 / friction=1 behavior, so restoring them must keep
 * those values even when the new-emitter defaults change.
 */
const LEGACY_EMIT_BOUNCE = 1.0;
const LEGACY_EMIT_FRICTION = 1.0;
const LEGACY_EMIT_DRAG = 0.0;

function parseSeqSteps(v: unknown, max: number): number[] {
  if (!Array.isArray(v)) {
    return [];
  }
  const out: number[] = [];
  for (const s of v) {
    if (typeof s === "number" && Number.isInteger(s) && s >= 0 && s < max && !out.includes(s)) {
      out.push(s);
    }
  }
  return out;
}

/**
 * An entry saved without `num` predates the per-emitter meter/num sequencer;
 * its seqSteps were indices on a canonical 32-division grid, which is
 * incompatible with the direct 0..num-1 cell indices used now, so the pattern
 * resets instead of being remapped.
 */
function parseEmitter(entry: unknown): Emitter | null {
  if (typeof entry !== "object" || entry === null) {
    return null;
  }
  const e = entry as Record<string, unknown>;
  const x = num(e.x);
  const y = num(e.y);
  const z = num(e.z);
  const rate = num(e.rate);
  const coneDeg = num(e.coneDeg);
  const speedMin = num(e.speedMin);
  const speedMax = num(e.speedMax);
  if (
    x === null || y === null || z === null || rate === null ||
    coneDeg === null || speedMin === null || speedMax === null
  ) {
    return null;
  }
  const qx = num(e.qx);
  const qy = num(e.qy);
  const qz = num(e.qz);
  const qw = num(e.qw);
  const rotation = qx !== null && qy !== null && qz !== null && qw !== null
    ? new THREE.Quaternion(qx, qy, qz, qw)
    : new THREE.Quaternion();
  const meter: Meter = e.meter === 3 ? 3 : 4;
  const storedNum = num(e.num);
  const seqNum = clampSeqNum(storedNum ?? SEQ_NUM_DEFAULT, meter);
  return {
    id: typeof e.id === "string" ? e.id : crypto.randomUUID(),
    position: new THREE.Vector3(x, y, z),
    rate,
    rotation,
    coneDeg,
    speedMin,
    speedMax,
    lifeMin: num(e.lifeMin) ?? LIFESPAN_MIN,
    lifeMax: num(e.lifeMax) ?? LIFESPAN_MAX,
    offset: num(e.offset) ?? 0,
    bounce: num(e.bounce) ?? LEGACY_EMIT_BOUNCE,
    friction: num(e.friction) ?? LEGACY_EMIT_FRICTION,
    drag: num(e.drag) ?? LEGACY_EMIT_DRAG,
    kind: e.kind === "sync" ? "sync" : "full",
    clockSync: e.kind === "sync",
    seqMode: e.kind === "sync",
    meter,
    num: seqNum,
    seqSteps: storedNum === null ? [] : parseSeqSteps(e.seqSteps, seqNum),
    pulseSpeedFromSettings: e.pulseSpeedFromSettings === true,
    gate: 1,
    name: typeof e.name === "string" ? e.name : "",
  };
}

function buildGizmo(emitter: Emitter, preview: boolean): THREE.Mesh {
  const geometry = new THREE.ConeGeometry(EMITTER_GIZMO_RADIUS, EMITTER_GIZMO_HEIGHT, 16);
  const material = new THREE.MeshBasicMaterial({
    color: EMITTER_COLOR,
    transparent: preview,
    opacity: preview ? 0.5 : 1,
    depthWrite: !preview,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.copy(emitter.position);
  mesh.quaternion.copy(emitter.rotation);
  return mesh;
}

/**
 * Builds a semi-transparent ghost gizmo for one serialized emitter entry
 * (paste drag preview). Returns null when the entry does not parse.
 */
export function ghostMeshFromEntry(entry: unknown): THREE.Mesh | null {
  const emitter = parseEmitter(entry);
  return emitter ? buildGizmo(emitter, true) : null;
}

export class EmitterField {
  readonly group: THREE.Group;
  private readonly emitters: Emitter[];
  private readonly meshes: THREE.Mesh[];
  private readonly livePose: Map<string, { pos: THREE.Vector3; quat: THREE.Quaternion }>;
  private preview: { params: EmitterParams; position: THREE.Vector3; mesh: THREE.Mesh } | null;
  private selected: Emitter | null;
  private readonly lastSelectedIdByKind: Map<"sync" | "full", string>;
  private undoRecorder: ((id: string) => void) | null = null;

  constructor() {
    this.group = new THREE.Group();
    this.emitters = [];
    this.meshes = [];
    this.livePose = new Map<string, { pos: THREE.Vector3; quat: THREE.Quaternion }>();
    this.preview = null;
    this.selected = null;
    this.lastSelectedIdByKind = new Map<"sync" | "full", string>();
  }

  /**
   * Lets the undo recorder capture an emitter's pre-mutation value the moment a
   * local edit touches it. Mutations on the remote-apply / undo-apply paths run
   * with the recorder suppressed, so only genuine local edits accumulate a step.
   */
  setUndoRecorder(recorder: (id: string) => void): void {
    this.undoRecorder = recorder;
  }

  add(emitter: Emitter): void {
    if (this.emitters.length >= MAX_EMITTERS) {
      console.warn(`EmitterField: MAX_EMITTERS (${MAX_EMITTERS}) reached, ignoring placement`);
      return;
    }
    this.undoRecorder?.(emitter.id);
    const mesh = buildGizmo(emitter, false);
    this.emitters.push(emitter);
    this.meshes.push(mesh);
    this.group.add(mesh);
  }

  list(): readonly Emitter[] {
    return this.emitters;
  }

  selectedIndex(): number {
    return this.selected ? this.emitters.indexOf(this.selected) : -1;
  }

  selectIndex(idx: number): void {
    if (idx < 0 || idx >= this.emitters.length) {
      this.deselect();
      return;
    }
    this.select(this.emitters[idx]);
  }

  /**
   * Distance-ordered hits under the ray paired with their hit distance, front→
   * back. Front/back faces of one gizmo dedupe (the nearer face's distance is
   * kept). The caller merges these with the object field's hits to cycle
   * selection across both types by distance.
   */
  orderedHits(raycaster: THREE.Raycaster): { item: Emitter; dist: number }[] {
    const hits = raycaster.intersectObjects(this.meshes, false);
    const ordered: { item: Emitter; dist: number }[] = [];
    for (const h of hits) {
      const idx = this.meshes.indexOf(h.object as THREE.Mesh);
      if (idx >= 0 && !ordered.some((o: { item: Emitter; dist: number }): boolean => o.item === this.emitters[idx])) {
        ordered.push({ item: this.emitters[idx], dist: h.distance });
      }
    }
    return ordered;
  }

  /**
   * World-space centers of every visible emitter, for the selection picker's
   * screen-space tap fallback (small emitters that project to a few pixels miss
   * the geometry raycast). Mesh world position is read so the on-screen center
   * matches what the user aims at. Filter mirrors `orderedHits` (raycast skips
   * invisible meshes) so the fallback offers nothing the raycast wouldn't.
   */
  pickCandidates(): { item: Emitter; center: THREE.Vector3 }[] {
    const out: { item: Emitter; center: THREE.Vector3 }[] = [];
    for (let i = 0; i < this.emitters.length; i++) {
      if (!this.meshes[i].visible) {
        continue;
      }
      out.push({ item: this.emitters[i], center: this.meshes[i].getWorldPosition(new THREE.Vector3()) });
    }
    return out;
  }

  select(emitter: Emitter): void {
    if (this.emitters.indexOf(emitter) < 0) {
      return;
    }
    this.selected = emitter;
    this.lastSelectedIdByKind.set(emitter.kind, emitter.id);
  }

  deselect(): void {
    this.selected = null;
  }

  clear(): void {
    this.deselect();
    for (const mesh of this.meshes) {
      this.disposeMesh(mesh);
    }
    this.meshes.length = 0;
    this.emitters.length = 0;
    this.livePose.clear();
  }

  /**
   * Overrides an emitter's gizmo and emit pose with an orbit-driven live pose
   * without touching the base `position`/`rotation` (the persisted source of
   * truth). `liveList()` feeds these poses to the particle system so spawned
   * particles originate from the moving source; clearLivePose snaps back to base.
   */
  setLivePose(id: string, pos: THREE.Vector3, quat: THREE.Quaternion): void {
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    if (idx < 0) {
      return;
    }
    let entry = this.livePose.get(id);
    if (!entry) {
      entry = { pos: new THREE.Vector3(), quat: new THREE.Quaternion() };
      this.livePose.set(id, entry);
    }
    entry.pos.copy(pos);
    entry.quat.copy(quat);
    this.meshes[idx].position.copy(pos);
    this.meshes[idx].quaternion.copy(quat);
  }

  clearLivePose(id: string): void {
    if (!this.livePose.delete(id)) {
      return;
    }
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    if (idx < 0) {
      return;
    }
    this.meshes[idx].position.copy(this.emitters[idx].position);
    this.meshes[idx].quaternion.copy(this.emitters[idx].rotation);
  }

  liveOrBasePosition(id: string): THREE.Vector3 | null {
    const em = this.emitters.find((e: Emitter): boolean => e.id === id);
    if (!em) {
      return null;
    }
    return this.livePose.get(id)?.pos ?? em.position;
  }

  /**
   * The emitter list with each orbit-driven emitter's position/rotation replaced
   * by its live pose (base emitters returned unchanged), for upload to the
   * particle system each frame while any emitter is in motion.
   */
  liveList(): readonly Emitter[] {
    if (this.livePose.size === 0) {
      return this.emitters;
    }
    return this.emitters.map((e: Emitter): Emitter => {
      const live = this.livePose.get(e.id);
      return live ? { ...e, position: live.pos, rotation: live.quat } : e;
    });
  }

  selectedEmitter(): Emitter | null {
    return this.selected;
  }

  lastSelectedOfKind(kind: "sync" | "full"): Emitter | null {
    const id = this.lastSelectedIdByKind.get(kind);
    if (id === undefined) {
      return null;
    }
    return this.emitters.find((e: Emitter): boolean => e.id === id) ?? null;
  }

  selectedMesh(): THREE.Mesh | null {
    if (!this.selected) {
      return null;
    }
    const idx = this.emitters.indexOf(this.selected);
    return idx >= 0 ? this.meshes[idx] : null;
  }

  meshById(id: string): THREE.Mesh | null {
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    return idx >= 0 ? this.meshes[idx] : null;
  }

  moveSelected(position: THREE.Vector3): void {
    const emitter = this.selected;
    if (!emitter) {
      return;
    }
    this.moveById(emitter.id, position);
  }

  moveById(id: string, position: THREE.Vector3): void {
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(id);
    this.emitters[idx].position.copy(position);
    this.meshes[idx].position.copy(position);
  }

  editSelected(patch: Partial<EmitterParams>): void {
    const emitter = this.selected;
    if (!emitter) {
      return;
    }
    const idx = this.emitters.indexOf(emitter);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(emitter.id);
    if (patch.rate !== undefined) {
      emitter.rate = patch.rate;
    }
    if (patch.rotation !== undefined) {
      emitter.rotation.copy(patch.rotation);
      this.meshes[idx].quaternion.copy(emitter.rotation);
    }
    if (patch.coneDeg !== undefined) {
      emitter.coneDeg = patch.coneDeg;
    }
    if (patch.speedMin !== undefined) {
      emitter.speedMin = patch.speedMin;
    }
    if (patch.speedMax !== undefined) {
      emitter.speedMax = patch.speedMax;
    }
    if (patch.lifeMin !== undefined) {
      emitter.lifeMin = patch.lifeMin;
    }
    if (patch.lifeMax !== undefined) {
      emitter.lifeMax = patch.lifeMax;
    }
    if (patch.offset !== undefined) {
      emitter.offset = patch.offset;
    }
    if (patch.bounce !== undefined) {
      emitter.bounce = patch.bounce;
    }
    if (patch.friction !== undefined) {
      emitter.friction = patch.friction;
    }
    if (patch.drag !== undefined) {
      emitter.drag = patch.drag;
    }
    if (patch.clockSync !== undefined) {
      emitter.clockSync = patch.clockSync;
    }
    if (patch.seqMode !== undefined) {
      emitter.seqMode = patch.seqMode;
    }
    if (patch.meter !== undefined) {
      emitter.meter = patch.meter;
    }
    if (patch.num !== undefined) {
      emitter.num = patch.num;
    }
    if (patch.seqSteps !== undefined) {
      emitter.seqSteps = [...patch.seqSteps];
    }
    if (patch.kind !== undefined) {
      emitter.kind = patch.kind;
    }
    if (patch.pulseSpeedFromSettings !== undefined) {
      emitter.pulseSpeedFromSettings = patch.pulseSpeedFromSettings;
    }
    if (patch.name !== undefined) {
      emitter.name = patch.name;
    }
  }

  deleteSelected(): void {
    const emitter = this.selected;
    if (!emitter) {
      return;
    }
    const idx = this.emitters.indexOf(emitter);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(emitter.id);
    this.deselect();
    this.disposeMesh(this.meshes[idx]);
    this.meshes.splice(idx, 1);
    this.emitters.splice(idx, 1);
  }

  removeById(id: string): void {
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    if (idx < 0) {
      return;
    }
    this.undoRecorder?.(id);
    if (this.selected === this.emitters[idx]) {
      this.deselect();
    }
    this.disposeMesh(this.meshes[idx]);
    this.meshes.splice(idx, 1);
    this.emitters.splice(idx, 1);
    this.livePose.delete(id);
  }

  /**
   * Replaces an emitter's data in place from its serialized form, keeping the same
   * array slot and identity so a remote edit to a selected emitter does not
   * deselect it (closing the panel). Rebuilds its gizmo mesh. Adds a new emitter
   * for an unknown id.
   */
  updateFromSerialized(id: string, entry: unknown): void {
    const idx = this.emitters.findIndex((e: Emitter): boolean => e.id === id);
    if (idx < 0) {
      this.restore([entry]);
      return;
    }
    const parsed = parseEmitter(entry);
    if (parsed === null) {
      return;
    }
    Object.assign(this.emitters[idx], parsed);
    this.disposeMesh(this.meshes[idx]);
    const mesh = buildGizmo(this.emitters[idx], false);
    this.meshes[idx] = mesh;
    this.group.add(mesh);
    this.livePose.delete(id);
  }

  beginPreview(params: EmitterParams, position: THREE.Vector3): void {
    this.cancelPreview();
    const mesh = buildGizmo({ id: "", ...params, position }, true);
    this.group.add(mesh);
    this.preview = { params: { ...params }, position: position.clone(), mesh };
  }

  updatePreview(position: THREE.Vector3): void {
    if (!this.preview) {
      return;
    }
    this.preview.position.copy(position);
    this.preview.mesh.position.copy(position);
  }

  commitPreview(): Emitter | null {
    if (!this.preview) {
      return null;
    }
    const { params, position } = this.preview;
    this.cancelPreview();
    const before = this.emitters.length;
    const emitter: Emitter = { id: crypto.randomUUID(), ...params, position: position.clone(), rotation: params.rotation.clone(), seqSteps: [...params.seqSteps] };
    this.add(emitter);
    if (this.emitters.length <= before) {
      return null;
    }
    track("first_place", { kind: emitter.kind });
    return emitter;
  }

  cancelPreview(): void {
    if (!this.preview) {
      return;
    }
    this.group.remove(this.preview.mesh);
    this.disposeMesh(this.preview.mesh);
    this.preview = null;
  }

  serialize(): unknown {
    return this.emitters.map((e: Emitter): Record<string, unknown> => this.serializeOne(e));
  }

  /** One emitter's serialized form by id (undo recorder's before-value capture), or null when absent. */
  serializeById(id: string): unknown | null {
    const e = this.emitters.find((em: Emitter): boolean => em.id === id);
    return e ? this.serializeOne(e) : null;
  }

  private serializeOne(e: Emitter): Record<string, unknown> {
    return {
      id: e.id,
      x: f32(e.position.x),
      y: f32(e.position.y),
      z: f32(e.position.z),
      rate: f32(e.rate),
      qx: f32(e.rotation.x),
      qy: f32(e.rotation.y),
      qz: f32(e.rotation.z),
      qw: f32(e.rotation.w),
      coneDeg: f32(e.coneDeg),
      speedMin: f32(e.speedMin),
      speedMax: f32(e.speedMax),
      lifeMin: f32(e.lifeMin),
      lifeMax: f32(e.lifeMax),
      offset: f32(e.offset),
      bounce: f32(e.bounce),
      friction: f32(e.friction),
      drag: f32(e.drag),
      clockSync: e.clockSync,
      seqMode: e.seqMode,
      meter: e.meter,
      num: e.num,
      seqSteps: [...e.seqSteps],
      kind: e.kind,
      pulseSpeedFromSettings: e.pulseSpeedFromSettings,
      name: e.name,
    };
  }

  restore(raw: unknown): void {
    if (!Array.isArray(raw)) {
      return;
    }
    for (const entry of raw) {
      const emitter = parseEmitter(entry);
      if (emitter) {
        this.add(emitter);
      }
    }
  }

  private disposeMesh(mesh: THREE.Mesh): void {
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
    this.group.remove(mesh);
  }
}
